import React from "react";
import { Link } from "react-router-dom";
import { ORIGINS, IMAGES } from "../../mock.de";
import { Reveal, SectionHeading, PageHero, Ornament } from "../../components/bits";
import VideoBackground from "../../components/VideoBackground";
import { Dna, Sun, Gem } from "lucide-react";

const symbolIcon = {
  "DNA-Helix": Dna,
  "Strahlende Sonne": Sun,
  "Unzerstörbarer Diamant": Gem,
};

const OriginsDe = () => {
  const { hero, intro, lineages, archetypes } = ORIGINS;

  return (
    <main className="video-page">
      <VideoBackground veil={0.55} />
      <PageHero
        image="/bridge_cosmo_tradition.webp"
        title={hero.title}
        sub={hero.sub}
        objectPosition="center 58%"
        overlay="linear-gradient(180deg, rgba(10,14,26,0.55), rgba(10,14,26,0.4)), linear-gradient(90deg, rgba(8,12,24,0.5) 0%, rgba(8,12,24,0.15) 60%, rgba(8,12,24,0) 100%)"
      />

      {/* Core philosophy blocks */}
      <section className="section bloom">
        <div className="container-page">
          <div className="container-narrow">
            {intro.map((block, i) => (
              <Reveal key={i} style={{ marginBottom: i < intro.length - 1 ? "3rem" : 0 }}>
                <h2 className="h-section" style={{ fontSize: "clamp(1.6rem, 2.8vw, 2.2rem)" }}>
                  {block.heading}
                </h2>
                <div className="prose-body" style={{ marginTop: "1.25rem" }}>
                  {block.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lineages */}
      <section className="section" style={{ background: "var(--paper-warm)", position: "relative", overflow: "hidden" }}>
        <div className="constellation" />
        <div className="container-page">
          <Reveal>
            <SectionHeading eyebrow="Ein offenbartes Paradigma" title={lineages.title} sub={lineages.sub} align="center" />
          </Reveal>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.5rem",
              marginTop: "3rem",
            }}
          >
            {lineages.items.map((item, i) => (
              <Reveal key={item.name} delay={i * 90} className="soft-card" style={{ padding: "2rem" }}>
                <div className="eyebrow" style={{ color: "var(--sky)", marginBottom: "0.5rem" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 style={{ fontSize: "1.5rem", marginBottom: "0.85rem" }}>{item.name}</h3>
                <p style={{ color: "var(--ink-soft)", fontSize: "0.98rem", lineHeight: 1.75 }}>{item.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Archetypes */}
      <section className="section bloom">
        <div className="container-page">
          <Reveal>
            <SectionHeading eyebrow="Die drei Körper der Wirklichkeit" title={archetypes.title} sub={archetypes.sub} align="center" />
          </Reveal>
          <div className="pillars-grid" style={{ marginTop: "3rem" }}>
            {archetypes.items.map((item, i) => {
              const Icon = symbolIcon[item.symbol] || Gem;
              return (
                <Reveal key={item.name} delay={i * 120} className="pillar soft-card">
                  <div
                    style={{
                      width: 78,
                      height: 78,
                      margin: "0 auto 1.25rem",
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: "linear-gradient(160deg, #fbf3da, #f3e7bd)",
                      border: "1px solid var(--gold-strong)",
                    }}
                  >
                    <Icon size={34} color="var(--gold-ink)" strokeWidth={1.4} />
                  </div>
                  <div className="eyebrow" style={{ color: "var(--gold-strong)", marginBottom: "0.3rem" }}>
                    {item.symbol}
                  </div>
                  <h3 style={{ fontSize: "1.45rem", marginBottom: "0.85rem" }}>{item.name}</h3>
                  <p style={{ color: "var(--ink-soft)", fontSize: "0.96rem", lineHeight: 1.75 }}>{item.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-tight" style={{ background: "var(--paper-warm)" }}>
        <div className="container-page">
          <Reveal className="container-narrow" style={{ textAlign: "center" }}>
            <Ornament style={{ marginBottom: "1.25rem", justifyContent: "center", display: "inline-flex" }} />
            <h2 className="h-section">Tritt in das Feld ein</h2>
            <p className="lead" style={{ marginTop: "1.25rem" }}>
              Wenn du in diesem Raum ruhst, wirst du mit kontemplativer Sorgfalt gehalten, getragen von offener
              wissenschaftlicher Neugier, gesegnet von alter spiritueller Linie und geboren aus der lebendigen Quelle
              universeller Stille.
            </p>
            <Link to="/de/kontakt" className="btn-gold" style={{ marginTop: "2rem" }}>
              Tritt in die Kohärenz ein
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default OriginsDe;
